import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { formatDistanceToNow } from 'date-fns';
import { useSnapshot } from 'valtio';
import { authStore } from '../../../../store/authStore';
import chatService, { ChatWithLastMessage } from '../../../services/chatService/ChatService';
import firebaseService from '../../../services/firebase/FirebaseService';
import { URLS } from '../../../../constants/urls';
import { isStaysSite } from '../../../constants/siteConfig';
import { toPublicS3ObjectUrl } from '../../../utils/s3PublicUrl';
import { isStayChat, stayChatSubtitle, stayChatTitle } from '../stayChat';

const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/120x80?text=Car';

const getInitials = (name?: string) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0][0].toUpperCase();
};

const getAvatarColor = (name?: string) => {
  if (!name) return 'var(--color-black)';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash) % 360;
  return `hsl(${h}, 70%, 50%)`;
};

const Container = styled.div`
  ${tw`mx-auto w-full max-w-screen-xl min-w-0 box-border px-4 pt-6 pb-8 sm:p-6 sm:pb-8 md:pb-16`}
`;

const Header = styled.div`
  ${tw`mb-6`}
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold text-text-primary`}
`;

const Subtitle = styled.p`
  ${tw`text-sm text-text-secondary mt-1`}
`;

const ChatCard = styled.div`
  ${tw`bg-white rounded-card p-4 mb-3 flex items-center cursor-pointer border border-border-default max-w-full min-w-0 box-border`}
  box-shadow: var(--shadow-card);
  transition:
    box-shadow var(--transition-normal),
    border-color var(--transition-normal);

  &:hover {
    box-shadow: var(--shadow-card-hover);
    border-color: var(--color-border-strong);
  }

  /* Prevent child elements from having their own hover effects */
  & > * {
    pointer-events: none;
  }
`;

const Avatar = styled.div<{ bgColor: string }>`
  ${tw`w-14 h-14 rounded-full mr-4 flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}
  background-color: ${props => props.bgColor};
`;

const ChatImage = styled.img`
  ${tw`w-14 h-14 rounded-full mr-4 object-cover bg-bg-secondary flex-shrink-0`}
`;

const ChatInfo = styled.div`
  ${tw`flex-1`}
`;

const ChatHeader = styled.div`
  ${tw`flex justify-between items-center mb-1`}
`;

const ChatName = styled.div`
  ${tw`text-base font-semibold text-text-primary`}
`;

const ChatTime = styled.div`
  ${tw`text-xs text-text-secondary`}
`;

const ChatPreview = styled.div`
  ${tw`text-sm text-text-secondary mb-1 truncate`}
`;

const CarInfo = styled.div`
  ${tw`text-xs text-text-secondary`}
`;

const UnreadBadge = styled.div`
  ${tw`min-w-[22px] h-[22px] rounded-full bg-black text-white text-xs font-semibold flex items-center justify-center px-2 ml-2`}
`;

const EmptyState = styled.div`
  ${tw`py-16 text-center`}
`;

const EmptyTitle = styled.div`
  ${tw`text-lg font-semibold text-text-secondary mb-2`}
`;

const EmptySubtitle = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

const LoadingWrapper = styled.div`
  ${tw`flex justify-center items-center py-16`}
`;

interface ChatListPageProps {
  role?: 'guest' | 'host';
}

const ChatListPage: React.FC<ChatListPageProps> = ({ role }) => {
  const navigate = useNavigate();
  const auth = useSnapshot(authStore);
  const userId = auth.user?.id;
  const [chats, setChats] = useState<ChatWithLastMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const renderChatTime = (date?: Date) => {
    if (!date) return '';
    return formatDistanceToNow(date, { addSuffix: true });
  };

  const handlePressChat = (chat: ChatWithLastMessage) => {
    navigate(`/chat/${chat.id}`, {
      state: { bookingId: chat.bookingId, stay: isStayChat(chat) || undefined },
    });
  };

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setError('Please sign in to view chats');
      return;
    }

    let unsubscribe: (() => void) | null = null;

    const initializeChats = async () => {
      try {
        // Ensure Firebase authentication before subscribing
        await firebaseService.ensureAuthenticated();

        // Subscribe to user chats (filtered by role if provided)
        unsubscribe = chatService.subscribeToUserChats(
          userId,
          (updatedChats: ChatWithLastMessage[]) => {
            setChats(updatedChats);
            setLoading(false);
          },
          role,
        );
      } catch (err: any) {
        setError(err.message || 'Failed to load chats. Please ensure you are signed in.');
        setLoading(false);
      }
    };

    initializeChats();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [userId, role]);

  if (loading) {
    return (
      <Container>
        <LoadingWrapper>
          <div>Loading chats...</div>
        </LoadingWrapper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <EmptyState>
          <EmptyTitle>Error</EmptyTitle>
          <EmptySubtitle>{error}</EmptySubtitle>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Messages</Title>
        <Subtitle>
          {isStaysSite()
            ? 'Questions and reservations, with hosts and guests'
            : 'Chat with hosts about your bookings'}
        </Subtitle>
      </Header>

      {chats.length === 0 ? (
        <EmptyState>
          <EmptyTitle>No conversations yet</EmptyTitle>
          <EmptySubtitle>
            {isStaysSite()
              ? 'Ask a host a question from a place’s page, or message them from your trip.'
              : 'You can start a conversation with a host from your booking details.'}
          </EmptySubtitle>
        </EmptyState>
      ) : (
        chats.map(chat => {
          const isGuest = chat.guestId === userId;
          const otherName = isGuest ? chat.hostDisplayName : chat.guestDisplayName;
          const participantName = otherName || 'User';
          const initials = getInitials(participantName);
          const avatarColor = getAvatarColor(participantName);
          // Spec 016 W27: a stay's chat is named for the place, and an inquiry says so.
          const stay = isStayChat(chat);

          return (
            <ChatCard
              key={chat.id}
              onClick={() => handlePressChat(chat)}
              data-testid="chat-list-item"
            >
              {stay && chat.listingImageUrl ? (
                <ChatImage src={toPublicS3ObjectUrl(chat.listingImageUrl)} alt="" />
              ) : (
                <Avatar bgColor={avatarColor}>{initials}</Avatar>
              )}
              <ChatInfo>
                <ChatHeader>
                  <ChatName>
                    {stay ? stayChatTitle(chat) : `Booking #${(chat.bookingId ?? '').slice(0, 8)}`}
                  </ChatName>
                  <ChatTime>{renderChatTime(chat.lastMessageAt)}</ChatTime>
                </ChatHeader>
                <ChatPreview>{chat.lastMessage || 'Say hello!'}</ChatPreview>
                <CarInfo>
                  {stay
                    ? stayChatSubtitle(chat, participantName)
                    : `${participantName} • ${[chat.carMake, chat.carModel].filter(Boolean).join(' ')}`}
                </CarInfo>
              </ChatInfo>
              {!!chat.unreadCount && chat.unreadCount > 0 && (
                <UnreadBadge>{chat.unreadCount > 9 ? '9+' : chat.unreadCount}</UnreadBadge>
              )}
            </ChatCard>
          );
        })
      )}
    </Container>
  );
};

export default ChatListPage;
